import { Trans, useLingui } from "@lingui/react/macro";
import type { TaskSuggestion } from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import { ArrowUpRight, Check, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

export function TaskSuggestionCard({
  block,
  messageId,
  target,
  onRefresh,
}: {
  block: TaskSuggestion;
  messageId: string;
  target: { botId: string } | { groupId: string };
  onRefresh: () => Promise<void>;
}) {
  const { t } = useLingui();
  const [pendingAction, setPendingAction] = useState<"start" | "dismiss" | null>(null);
  const busy = pendingAction !== null;
  const [status, setStatus] = useState(block.status);
  const [error, setError] = useState<string | null>(null);
  const effective =
    block.status === "started" || block.status === "dismissed"
      ? block.status
      : status === "started" || status === "dismissed"
        ? status
        : block.status;
  if (effective === "dismissed") return null;
  async function choose(action: "start" | "dismiss") {
    setPendingAction(action);
    setError(null);
    try {
      const result = await rpc.threads.suggestion({ ...target, messageId, action });
      setStatus(result.status);
      await onRefresh();
    } catch (err) {
      setError(errorText(err, t`Could not update suggestion`));
    } finally {
      setPendingAction(null);
    }
  }
  return (
    <section
      data-testid="task-suggestion"
      className="ai-task-enter max-w-md rounded-2xl border border-border px-4 py-3"
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <ArrowUpRight size={16} aria-hidden="true" />
        {block.title}
      </div>
      <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
        {block.prompt}
      </p>
      {effective === "started" ? (
        <p className="ai-task-status mt-2 flex items-center gap-1.5 text-xs text-success">
          <Check size={14} aria-hidden="true" />
          <Trans>Started</Trans>
        </p>
      ) : (
        <div className="mt-3 flex justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            disabled={busy || effective === "starting"}
            onClick={() => void choose("dismiss")}
          >
            {pendingAction === "dismiss" ? (
              <LoaderCircle size={14} aria-hidden="true" className="motion-safe:animate-spin" />
            ) : null}
            <Trans>Dismiss</Trans>
          </Button>
          <Button size="sm" disabled={busy} onClick={() => void choose("start")}>
            {pendingAction === "start" ? (
              <LoaderCircle size={14} aria-hidden="true" className="motion-safe:animate-spin" />
            ) : null}
            {block.status === "starting" ? t`Retry start` : t`Start`}
          </Button>
        </div>
      )}
      {error ? (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </section>
  );
}
